/**
 * Quality-loop API client — the founder's configuration + observation surface
 * for the cross-vendor review loop (migration 0126, gateway quality-loop.ts).
 *
 * ── WHERE THESE NAMES COME FROM ──
 * Every type below is the camelCase mirror of something that already exists
 * server-side; nothing here is invented vocabulary:
 *
 *   QualityLoopConfig / ResolvedLimit  → quality-loop.ts (same field names)
 *   QualityLoopCycleSummary            → quality-loop.ts CycleRow + the stop_*
 *                                        columns of chaos.quality_loop_cycles
 *   QualityCriterion                   → chaos.quality_criteria (0126 §2)
 *   QualityVerdictDetail               → chaos.quality_verdicts (0126 §5)
 *   QualityVerdictCriterion            → chaos.quality_verdict_criteria (§5.1)
 *
 * The resolution ladder is COALESCE(card, account, system default) and every
 * number carries its `source`. That triple is not decoration: when a loop
 * stops, the founder must be able to read the limit AND its provenance, and
 * neither may be re-derived from config that has since changed.
 *
 * ── DEGRADATION IS PART OF THE CONTRACT ──
 * QUALITY_LOOP_ENABLED ships default OFF and these routes may not be mounted
 * at all on a given deploy. `isQualityLoopUnavailable` classifies that case so
 * every consumer can render nothing instead of an error card. A feature that
 * is switched off must be invisible, not broken.
 *
 * Paths are written WITHOUT the `/api` prefix — apiJson prepends
 * env.VITE_API_BASE_URL, matching the cards-v2 client idiom.
 */
import type {
  QualityLimitSource,
  QualityVendor,
  QualityVerdictStatus,
  QualityVerificationStatus,
  QualityVerifyKind,
} from '../quality-loop/display';

import { ApiError, apiJson, isRouteMissing } from './client';

// ── Resolved limits ───────────────────────────────────────────────────

/** One limit plus WHERE it came from. Mirrors quality-loop.ts ResolvedLimit. */
export interface ResolvedLimit<T> {
  value: T;
  source: QualityLimitSource;
}

/** Mirrors quality-loop.ts QualityLoopConfig. */
export interface QualityLoopConfig {
  enabled: boolean;
  /** true only when chaos.cards.quality_loop_enabled is explicitly TRUE. */
  cardOptIn: boolean;
  maxAttempts: ResolvedLimit<number>;
  watcherVendor: ResolvedLimit<QualityVendor>;
  watcherModel: ResolvedLimit<string | null>;
  watcherTokenCap: ResolvedLimit<number>;
  watcherTimeoutSec: ResolvedLimit<number>;
  auditSamplePct: number;
  makerVendor: string | null;
  makerModel: string | null;
}

/**
 * The RAW per-card columns (chaos.cards.quality_loop_*). null everywhere means
 * "inherit", exactly like cards.card_max_consecutive_runs. Separate from the
 * resolved config because the editor edits the override, not the result.
 */
export interface QualityLoopOverrides {
  enabled: boolean | null;
  maxAttempts: number | null;
  watcherVendor: QualityVendor | null;
  watcherModel: string | null;
  watcherTokenCap: number | null;
  watcherTimeoutSec: number | null;
}

/** The account rung of the ladder (chaos.accounts.quality_loop_*). */
export interface QualityLoopAccountDefaults {
  enabled: boolean;
  maxAttempts: number;
  watcherVendor: QualityVendor | null;
  watcherModel: string | null;
  watcherTokenCap: number;
  watcherTimeoutSec: number;
  auditSamplePct: number;
}

// ── Criteria ──────────────────────────────────────────────────────────

/**
 * One acceptance criterion. `verifyKind` null = the reviewer reads and reasons
 * only; anything else is a check the WATCHER container executes against the
 * :ro workspace.
 *
 * verifyPayload keys are SNAKE_CASE — they are copied verbatim from migration
 * 0126 §2, which is the shape the watcher harness switches on. This is the one
 * place in the client where camelCasing a field would silently disable a check
 * rather than fail loudly, so the payload is passed through untouched:
 *   command     {command, cwd?, timeout_sec?, expect_exit_code?, expect_stdout_matches?}
 *   http        {url, method?, expect_status?, expect_body_matches?}
 *   screenshot  {url, viewport:{width,height}, theme?, expect?}
 *   file_exists {path, expect_matches?}
 *   artifact    {path, min_bytes?}
 */
export interface QualityCriterion {
  id: string;
  criterionKey: string;
  ordinal: number;
  text: string;
  isRequired: boolean;
  verifyKind: QualityVerifyKind | null;
  verifyPayload: Record<string, unknown>;
  notes: string | null;
}

/** A published (frozen) criteria version. */
export interface QualityCriteriaVersion {
  id: string;
  versionNo: number;
  frozenAt: string | null;
  changeSummary: string | null;
  criteria: QualityCriterion[];
}

/** What the editor submits. No id — publishing mints a new frozen version. */
export interface QualityCriterionInput {
  criterionKey: string;
  ordinal: number;
  text: string;
  isRequired: boolean;
  verifyKind: QualityVerifyKind | null;
  verifyPayload: Record<string, unknown>;
  notes: string | null;
}

export interface PublishCriteriaInput {
  changeSummary: string | null;
  criteria: QualityCriterionInput[];
}

// ── Cycles ────────────────────────────────────────────────────────────

/**
 * Mirrors CycleRow plus the stop/provenance columns the UI must surface.
 * `attemptsUsed` counts REAL verdicts only — a watcher crash does not consume
 * an attempt (quality_verdicts.is_verdict is the discriminator), which is why
 * `watcherErrorCount` is reported separately.
 */
export interface QualityLoopCycleSummary {
  id: string;
  cardId: string;
  cycleNo: number;
  state: string;
  attemptsUsed: number;
  /** The attempt currently in flight, when one is. */
  currentAttemptNo: number | null;
  maxAttempts: number;
  maxAttemptsSource: QualityLimitSource;
  watcherVendor: QualityVendor;
  watcherVendorSource: QualityLimitSource | null;
  watcherModel: string | null;
  watcherTokenCap: number | null;
  watcherTokenCapSource: QualityLimitSource | null;
  watcherTimeoutSec: number | null;
  makerVendor: string | null;
  makerModel: string | null;
  watcherErrorCount: number;
  maxWatcherErrors: number;
  criteriaVersionId: string | null;
  criteriaFrozenAt: string | null;
  stopReason: string | null;
  stopLimitValue: number | null;
  stopLimitSource: QualityLimitSource | null;
  stopDetail: string | null;
  pausedReason: string | null;
  /** Non-null ⇒ a watcher container is believed to be in flight. */
  watcherStartedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  passedAt: string | null;
  /** Watcher tokens spent by THIS cycle. */
  tokensTotal: number;
}

/** GET /cards/:cardId/quality-loop */
export interface CardQualityLoop {
  cardId: string;
  /** Needed to address the org-scoped cycle + verdict routes below. */
  orgId: string;
  /** The gateway's QUALITY_LOOP_ENABLED flag. false ⇒ configurable but inert. */
  flagEnabled: boolean;
  config: QualityLoopConfig;
  overrides: QualityLoopOverrides;
  accountDefaults: QualityLoopAccountDefaults;
  /** The live (frozen, unsuperseded) criteria version — null = none published. */
  criteria: QualityCriteriaVersion | null;
  /** The live cycle (running|paused), else the most recent one, else null. */
  cycle: QualityLoopCycleSummary | null;
  /** Watcher tokens spent across EVERY cycle of this card. */
  tokensSpentTotal: number;
}

/** PATCH body. An ABSENT key leaves the column alone; an explicit null clears
 *  the override so the card falls back to the account default. */
export type UpdateQualityLoopInput = Partial<QualityLoopOverrides>;

// ── Verdict detail (the timeline's expandable half) ───────────────────

/** One row of chaos.quality_verdict_criteria. */
export interface QualityVerdictCriterion {
  criterionKey: string;
  criterionText: string;
  isRequired: boolean;
  ordinal: number;
  met: boolean;
  reason: string | null;
  verificationStatus: QualityVerificationStatus;
  verifyKind: QualityVerifyKind | null;
  /** What was actually run/observed, e.g. {kind, command, exit_code, …}. */
  evidence: Record<string, unknown>;
  evidenceSummary: string | null;
}

/** GET /quality-loop/verdicts/:verdictId */
export interface QualityVerdictDetail {
  id: string;
  cycleId: string;
  attemptId: string;
  attemptNo: number;
  tryNo: number;
  status: QualityVerdictStatus;
  isVerdict: boolean;
  summary: string | null;
  reasons: string | null;
  criteriaTotal: number;
  criteriaMet: number;
  criteriaUnmet: number;
  /** Met by the maker's CLAIM with no check run — how much was taken on faith. */
  criteriaUnverified: number;
  criteria: QualityVerdictCriterion[];
  deterministicVetoes: Array<{ code: string; note?: string | null }>;
  vetoForcedFail: boolean;
  watcherVendor: QualityVendor;
  watcherModel: string | null;
  makerVendor: string | null;
  makerModel: string | null;
  crossVendor: boolean;
  tokensInput: number;
  tokensOutput: number;
  tokensCacheRead: number;
  tokensCacheCreation: number;
  /** Always read THIS for cost; the split is only meaningful when non-zero. */
  tokensTotal: number;
  tokenCap: number | null;
  tokenCapSource: QualityLimitSource | null;
  durationMs: number | null;
  errorKind: string | null;
  errorMessage: string | null;
  /** Truncated raw watcher stdout — the judge's own transcript. */
  rawOutput: string | null;
  startedAt: string | null;
  endedAt: string | null;
}

// ── Board badge summaries ─────────────────────────────────────────────

/**
 * ONE request per board, not one per card. A board can hold a hundred cards
 * and the badge is decoration on a scan surface; fanning out a request per
 * card to decorate it would be the most expensive thing on the board.
 */
export interface QualityLoopBoardEntry {
  cardId: string;
  cycleId: string;
  state: string;
  attemptsUsed: number;
  currentAttemptNo: number | null;
  maxAttempts: number;
  maxAttemptsSource: QualityLimitSource;
  stopReason: string | null;
  stopLimitValue: number | null;
  stopLimitSource: QualityLimitSource | null;
  pausedReason: string | null;
  watcherStartedAt: string | null;
}

export interface QualityLoopBoardSummary {
  boardId: string;
  entries: QualityLoopBoardEntry[];
}

// ── Routes ────────────────────────────────────────────────────────────
//
// TWO conventions, each matching the precedent it belongs to. They are hoisted
// here so the whole HTTP contract is one readable block rather than string
// literals scattered through the client.
//
//  • PER-CARD CONFIG  →  /api/cards/:cardId/quality-loop[...]
//    Mirrors cards-v2's /api/cards/:cardId/run-ceiling, which is the existing
//    per-card limit-with-provenance endpoint this one is modelled on.
//
//  • VERDICT READS + HUMAN WRITES  →  /api/orgs/:orgId/quality/[...]
//    Mirrors quality-loop-routes.ts, which already serves
//    /api/orgs/:orgId/quality/metrics, /audit-queue and
//    /verdicts/:verdictId/review. That file is mounted ONLY inside index.ts's
//    `if (QUALITY_LOOP_ENABLED())` block, which is precisely why a flag-OFF
//    deploy answers 404 here and why every consumer treats 404 as "render
//    nothing" rather than as an error.
export const qualityLoopRoutes = {
  card: (cardId: string) => `/cards/${cardId}/quality-loop`,
  cardCriteria: (cardId: string) => `/cards/${cardId}/quality-loop/criteria`,
  board: (boardId: string) => `/boards/${boardId}/quality-loop`,
  cyclePause: (orgId: string, cycleId: string) =>
    `/orgs/${orgId}/quality/cycles/${cycleId}/pause`,
  cycleResume: (orgId: string, cycleId: string) =>
    `/orgs/${orgId}/quality/cycles/${cycleId}/resume`,
  verdict: (orgId: string, verdictId: string) => `/orgs/${orgId}/quality/verdicts/${verdictId}`,
} as const;

// ── Client ────────────────────────────────────────────────────────────

export const qualityLoopApi = {
  getForCard(cardId: string): Promise<CardQualityLoop> {
    return apiJson<CardQualityLoop>(qualityLoopRoutes.card(cardId));
  },
  update(cardId: string, input: UpdateQualityLoopInput): Promise<CardQualityLoop> {
    return apiJson<CardQualityLoop>(qualityLoopRoutes.card(cardId), {
      method: 'PATCH',
      json: input,
    });
  },
  /** Publishes a NEW frozen version. Running cycles keep their pinned snapshot. */
  publishCriteria(cardId: string, input: PublishCriteriaInput): Promise<CardQualityLoop> {
    return apiJson<CardQualityLoop>(qualityLoopRoutes.cardCriteria(cardId), {
      method: 'PUT',
      json: input,
    });
  },
  pause(orgId: string, cycleId: string): Promise<{ ok: boolean; error?: string }> {
    return apiJson<{ ok: boolean; error?: string }>(
      qualityLoopRoutes.cyclePause(orgId, cycleId),
      { method: 'POST', json: {} },
    );
  },
  /**
   * RESUMES from where it stopped; it never restarts. `grantAttempts` widens
   * the budget of the SAME cycle — same cycle_no, same iteration_chain_id, same
   * criteria snapshot. That is why "grant more attempts" is this call and not a
   * new cycle.
   */
  resume(
    orgId: string,
    cycleId: string,
    grantAttempts = 0,
  ): Promise<{ ok: boolean; decision?: string; error?: string }> {
    return apiJson<{ ok: boolean; decision?: string; error?: string }>(
      qualityLoopRoutes.cycleResume(orgId, cycleId),
      { method: 'POST', json: grantAttempts > 0 ? { grantAttempts } : {} },
    );
  },
  getVerdict(orgId: string, verdictId: string): Promise<QualityVerdictDetail> {
    return apiJson<QualityVerdictDetail>(qualityLoopRoutes.verdict(orgId, verdictId));
  },
  getBoardSummary(boardId: string): Promise<QualityLoopBoardSummary> {
    return apiJson<QualityLoopBoardSummary>(qualityLoopRoutes.board(boardId));
  },
};

export const qualityLoopKeys = {
  all: ['quality-loop'] as const,
  card: (cardId: string) => [...qualityLoopKeys.all, 'card', cardId] as const,
  verdict: (verdictId: string) => [...qualityLoopKeys.all, 'verdict', verdictId] as const,
  board: (boardId: string) => [...qualityLoopKeys.all, 'board', boardId] as const,
};

/**
 * "This deploy does not have the quality loop" vs "something broke".
 *
 * 404 is the honest answer from a gateway whose quality-loop routes are not
 * mounted (flag OFF), and 501 from one that mounts them but refuses. Both mean
 * RENDER NOTHING. Anything else — 403, 500, a network failure — is a real
 * error and must not be swallowed into silence, because a founder who cannot
 * see the loop's state is exactly the person this feature exists for.
 */
export function isQualityLoopUnavailable(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 501);
}

/**
 * ONE probe per page session, not one per board visit.
 *
 * QUALITY_LOOP_ENABLED ships default OFF, so on most deployments these routes
 * are simply not mounted. The board badge is the only surface that would ask
 * unprompted — every card on a board issues the same query — and an errored
 * react-query entry holds no data, which makes it permanently stale and
 * therefore refetched on every remount. Navigating between boards would then
 * re-probe a 404 forever.
 *
 * So the FIRST unavailable answer latches, and every later query is disabled
 * before it is issued. Deliberately NOT a build flag: gating this behind a
 * VITE_ flag would make the whole feature invisible on the day the gateway
 * routes land, which is the one failure mode worse than a wasted request. The
 * latch is per page load, so a deploy that turns the loop ON is picked up by
 * the next reload with no rebuild.
 */
let unavailableLatch = false;

/**
 * Record a failure. The LATCH is stricter than `isQualityLoopUnavailable`, and
 * the gap between them is deliberate.
 *
 * Rendering nothing for any 404 is per-query and reversible. The latch is
 * global and sticky for the whole page load, so it must only be set by a 404
 * that is a fact about the DEPLOYMENT. Since 2026-08-01 a card in a Workspace
 * the caller is not in also answers 404 — that is the point of the shared gate
 * — and one such card would otherwise switch the quality loop off for every
 * other card on the page, including the ones the caller owns. `isRouteMissing`
 * is the measured separator: only the gateway's own not-found handler echoes
 * the path. A 500 or a network blip still must not latch, and does not.
 */
export function noteQualityLoopError(error: unknown): void {
  if (isRouteMissing(error) || (error instanceof ApiError && error.status === 501)) {
    unavailableLatch = true;
  }
}

/** True once this deployment has answered "these routes do not exist". */
export function qualityLoopKnownUnavailable(): boolean {
  return unavailableLatch;
}

/** Test seam. */
export function __resetQualityLoopAvailability(): void {
  unavailableLatch = false;
}

/**
 * react-query `retry` for every quality-loop query. Without this a flag-OFF
 * deploy pays three retries per card for a 404 that will never become a 200.
 */
export function qualityLoopRetry(failureCount: number, error: unknown): boolean {
  if (isQualityLoopUnavailable(error)) {
    noteQualityLoopError(error);
    return false;
  }
  return failureCount < 2;
}
